import fp from 'lodash/fp';
import T from 'prop-types';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dropdown, Table } from 'react-bootstrap';
import classNames from 'classnames';
import { format } from 'date-fns';

import { USER_TYPE } from '../../../constants/user';
import UserAvatar from '../../../pages/profile/UserAvatar';
import { hideModal, showModal } from '../../../redux/actions';
import text from '../../../text';
import { getProjectLatestJob } from '../../../utilities/project';
import { canGiveTrial, getUserType, isStaff } from '../../../utilities/user';
import MeatballDropdown from '../../dropdown/MeatballDropdown';
import { GIVE_USER_TRIAL_MODAL } from '../../modal/GiveUserTrialModal';

const USER_TYPE_LABEL = {
  [USER_TYPE.FREE]: text('free'),
  [USER_TYPE.TRIAL]: text('trial'),
  [USER_TYPE.ESSENTIALS]: text('essentials'),
  [USER_TYPE.LARKI_SUPPORT]: text('larkiSupport'),
  [USER_TYPE.GOVERNMENT]: text('governmentFullWord'),
};

const formatDate = (date) =>
  date ? format(new Date(date), 'd MMM yyyy') : null;

const InvitationTable = ({
  invitations,
  project,
  resendInvitation,
  removeProjectUser,
  updateUserProjectVisibility,
}) => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.profileReducer.userProfile);
  const job = getProjectLatestJob(project);

  const showInvite = (invite) =>
    isStaff(currentUser.role) || !isStaff(invite.user.role);
  const isCurrentUser = (invite) => invite.user.id === currentUser.id;
  const showVisibilityMenu = (invite) =>
    isStaff(currentUser.role) && !isStaff(invite.user.role);
  const showHiddenIndicator = (invite) =>
    (isStaff(currentUser.role) && invite.visibility === 'staff') ||
    isStaff(invite.user.role);

  const latestStatusUpdatedAt = (invite) => {
    if (invite?.updated_at) return formatDate(invite.updated_at);
    if (invite?.user?.updated_at) return formatDate(invite.user.updated_at);
    return null;
  };

  const handleGiveTrial = (user) => {
    dispatch(
      showModal(GIVE_USER_TRIAL_MODAL, {
        user,
        onHide: () => dispatch(hideModal(GIVE_USER_TRIAL_MODAL)),
      })
    );
  };

  const groupedInvitations = fp.flow(
    fp.map((invite) => ({ ...invite, user_type: getUserType(invite.user) })),
    fp.groupBy('user_type'),
    fp.mapValues((invites) => invites.filter(showInvite)),
    fp.pickBy((invites) => !fp.isEmpty(invites))
  )(invitations);

  const renderInviteRow = (invite) => (
    <tr key={invite.user.id}>
      <td className='UserNameCell d-flex flex-row'>
        <div className='d-flex flex-row'>
          <UserAvatar
            profileImage={invite.user.profile_image}
            className='mr-3'
          />
          <div className='d-flex flex-column justify-content-center name-wrapper'>
            <p className='m-0 name font-14 font-weight--600 text-break'>
              {invite.user.full_name}
            </p>
            <p
              className={classNames('m-0 email font-12', {
                'text-truncate': invite.user.full_name,
                'text-break': !invite.user.full_name,
              })}
            >
              {invite.user.email}
            </p>
          </div>
        </div>
      </td>
      <td className='StatusCell font-italic font-weight--400'>
        <p className='m-0'>{fp.capitalize(invite?.invite_status)}</p>
        <p className='m-0'>{latestStatusUpdatedAt(invite)}</p>
      </td>
      <td className='MembershipCell px-0'>
        <p className='m-0 font-weight--600 font-14'>
          {USER_TYPE_LABEL[getUserType(invite.user)]}
          {showHiddenIndicator(invite) && ` (${text('Hidden')})`}
        </p>
      </td>
      <td className='ActionCell'>
        <MeatballDropdown>
          <Dropdown.Item
            disabled={invite?.invite_status !== 'pending'}
            onClick={() => resendInvitation(invite?.user?.email)}
          >
            {text('resendInvitation')}
          </Dropdown.Item>
          {canGiveTrial(currentUser, invite.user) && (
            <Dropdown.Item onClick={() => handleGiveTrial(invite.user)}>
              {text('addTrial')}
            </Dropdown.Item>
          )}
          <Dropdown.Item
            disabled={job?.owner_user_id === invite.user.id}
            onClick={() => removeProjectUser(project, invite)}
          >
            {isCurrentUser(invite)
              ? text('leaveProject')
              : text('removeProjectUser')}
          </Dropdown.Item>
          {showVisibilityMenu(invite) && (
            <Dropdown.Item onClick={() => updateUserProjectVisibility(invite)}>
              {invite.visibility === 'public' ? text('Hide') : text('Show')}
            </Dropdown.Item>
          )}
        </MeatballDropdown>
      </td>
    </tr>
  );

  return (
    <Table className='Table InvitationTable' responsive>
      <thead>
        <tr>
          <th>{text('projectUsers')}</th>
          <th>{text('status')}</th>
          <th className='px-0'>{text('member')}</th>
          <th className='ActionCell'>{text('actions')}</th>
        </tr>
      </thead>
      <tbody>
        {fp.flow(fp.map((invites) => invites.map(renderInviteRow)))(
          groupedInvitations
        )}
      </tbody>
    </Table>
  );
};

InvitationTable.propTypes = {
  invitations: T.arrayOf(
    T.shape({
      user: T.shape({
        id: T.string.isRequired,
        email: T.string.isRequired,
        full_name: T.string,
        profile_image: T.string,
        role: T.string,
        updated_at: T.string,
      }).isRequired,
      invite_status: T.string,
      updated_at: T.string,
      visibility: T.string,
    })
  ).isRequired,
  project: T.object,
  resendInvitation: T.func.isRequired,
  removeProjectUser: T.func.isRequired,
  updateUserProjectVisibility: T.func.isRequired,
};

export default InvitationTable;
